import { useCallback, useState } from 'react';
import api from '../api/api';
import { notify } from '../utils/notifications';

const CLOSE_CONFIRM_MESSAGE =
    'Closing this case will archive it and permanently delete patient data. This cannot be undone. Continue?';

/**
 * @param {object} args
 * @param {string} args.caseId
 * @param {string} args.currentStatus
 * @param {(status: string) => void} args.onOptimisticChange
 * @param {string} [args.ownerUid] the case owner when acting on a colleague's
 *   case (B4). Callers pass it only when it differs from the signed-in user;
 *   the server resolves the path and enforces same-facility membership.
 */
export function useUpdateAdmissionStatus({ caseId, currentStatus, onOptimisticChange, ownerUid }) {
    const [isPending, setIsPending] = useState(false);

    const change = useCallback(async (newStatus) => {
        if (newStatus === currentStatus) return;

        if (newStatus === 'Closed') {
            const ok = window.confirm(CLOSE_CONFIRM_MESSAGE);
            if (!ok) return;
        }

        onOptimisticChange(newStatus);
        setIsPending(true);

        try {
            const body = new URLSearchParams();
            body.append('case_id', caseId);
            if (ownerUid) body.append('owner_uid', ownerUid);
            body.append('admission_status', newStatus);
            await api.post('/case/set-admission-status', body, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                withCredentials: true,
            });
        } catch (err) {
            notify.error('Failed to update admission status');
            onOptimisticChange(currentStatus);
        } finally {
            setIsPending(false);
        }
    }, [caseId, currentStatus, onOptimisticChange, ownerUid]);

    return { change, isPending };
}

export default useUpdateAdmissionStatus;
